import * as Sentry from '@sentry/vue'

// Vue & Plugins
import { computed, ref, Ref } from 'vue'
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'

// Models
import { User } from '../users/models/user'

// Services
import { show, logOut } from './authUserService'
import { login, twoFactorChallenge } from '../auth/authService'

// Stores
import { useAuthSiteStore } from './authSiteStore'

export const useAuthUserStore = defineStore(
    'AuthUserStore',
    () => {
        const authSiteStore = useAuthSiteStore()
        const { current, currentConfig, currentPermissions } = storeToRefs(authSiteStore)
        const { fetchSites, fetchCurrentSite, fetchCurrentSiteConfig, fetchCurrentSitePermissions } = authSiteStore

        const router = useRouter()
        const queryClient = useQueryClient()

        const hasCheckedSession = ref(false)
        const isCheckingSession = ref(false)

        async function checkSession(): Promise<void> {
            if (isCheckingSession.value) {
                return
            }

            isCheckingSession.value = true

            try {
                await show()
            } finally {
                hasCheckedSession.value = true

                isCheckingSession.value = false
            }
        }

        const isFetching = ref(false)
        const user: Ref<User | null> = ref(null)

        const fetch = async () => {
            if (isFetching.value) {
                console.log('Already fetching user, skipping...')
                return
            }

            isFetching.value = true

            try {
                const result = await show()

                user.value = result.user
            } catch (error) {
                console.error(error)

                Sentry.captureException(error)
            } finally {
                isFetching.value = false
            }
        }

        const isSignInLoading = ref(false)
        const signIn = async ({ email, password }: { email: string, password: string }) => {
            isSignInLoading.value = true

            try {
                const response = await login(email, password)

                if (response.two_factor) {
                    router.push({ name: '/(guest)/two-factor' })
                } else {
                    await initialiseAuth()

                    router.push({ name: '/(app)/dashboard/' })
                }
            } finally {
                isSignInLoading.value = false
            }
        }

        const isSignInTwoFactorLoading = ref(false)
        const signInTwoFactor = async ({ code }: { code: string }) => {
            isSignInTwoFactorLoading.value = true

            try {
                await twoFactorChallenge(code)

                await initialiseAuth()

                router.push({ name: '/(app)/dashboard/' })
            } finally {
                isSignInTwoFactorLoading.value = false
            }
        }

        const signOut = async () => {
            queryClient.clear()

            user.value = null

            await logOut()

            await router.push({ name: '/(guest)/login' })

            authSiteStore.reset()
            isInitialisingSite.value = true
        }

        const isInitialising = ref(true)
        const isAuthenticated = computed(() => !!user.value)

        const initialiseAuth = async () => {
            isInitialising.value = true

            try {
                await checkSession()

                await fetch()
            } catch (error) {
                console.error('Error during auth initialisation:', error)

                user.value = null
            } finally {
                isInitialising.value = false
            }
        }

        const isInitialisingSite = ref(true)
        const isSiteInitialised = computed(() => !!current.value && !!currentConfig.value && currentPermissions.value.length > 0)
        const initialiseSite = async () => {
            isInitialisingSite.value = true

            try {
                await fetchSites()

                await fetchCurrentSite()
                await fetchCurrentSiteConfig()
                await fetchCurrentSitePermissions()
            } finally {
                isInitialisingSite.value = false
            }
        }

        return {
            isCheckingSession,
            hasCheckedSession,

            isSignInLoading,
            isSignInTwoFactorLoading,
            signIn,
            signInTwoFactor,

            isInitialising,
            isAuthenticated,
            initialiseAuth,

            isInitialisingSite,
            isSiteInitialised,
            initialiseSite,

            checkSession,
            isFetching,
            user,
            fetch,
            signOut,
        }
    },
)

if (import.meta.hot !== null && import.meta.hot !== undefined) {
    import.meta.hot.accept(
        acceptHMRUpdate(
            useAuthUserStore,
            import.meta.hot,
        ),
    )
}
